import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Search, 
  History, 
  UserCheck, 
  UserX, 
  ArrowUpCircle,
  ArrowDownCircle,
  Shield,
  Clock,
  Filter,
  RefreshCw,
  Crown,
  FileText,
  Settings,
  Activity
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const ACTION_ICONS = {
  user_approved: { icon: UserCheck, color: 'text-green-600', bg: 'bg-green-100' },
  user_denied: { icon: UserX, color: 'text-red-600', bg: 'bg-red-100' },
  user_promoted_manager: { icon: ArrowUpCircle, color: 'text-purple-600', bg: 'bg-purple-100' },
  user_demoted_pm: { icon: ArrowDownCircle, color: 'text-orange-600', bg: 'bg-orange-100' },
  user_promoted_superadmin: { icon: Crown, color: 'text-amber-600', bg: 'bg-amber-100' },
  user_activated: { icon: UserCheck, color: 'text-green-600', bg: 'bg-green-100' },
  user_deactivated: { icon: UserX, color: 'text-gray-600', bg: 'bg-gray-100' },
  user_login: { icon: Shield, color: 'text-blue-600', bg: 'bg-blue-100' },
  domain_added: { icon: Settings, color: 'text-lime-600', bg: 'bg-lime-100' },
  domain_removed: { icon: Settings, color: 'text-red-600', bg: 'bg-red-100' },
  project_created: { icon: FileText, color: 'text-blue-600', bg: 'bg-blue-100' },
  session_bulk_imported: { icon: FileText, color: 'text-indigo-600', bg: 'bg-indigo-100' },
};

const ACTION_LABELS = {
  user_login: 'User Login',
  user_logout: 'User Logout',
  user_created: 'User Created',
  user_approved: 'User Approved',
  user_denied: 'User Denied',
  user_updated: 'User Updated',
  user_activated: 'User Activated',
  user_deactivated: 'User Deactivated',
  user_role_changed: 'Role Changed',
  user_promoted_manager: 'Promoted to Manager',
  user_demoted_pm: 'Demoted to PM',
  user_promoted_superadmin: 'Promoted to Super Admin',
  user_partners_assigned: 'Partners Assigned',
  domain_added: 'Domain Added',
  domain_removed: 'Domain Removed',
  settings_updated: 'Settings Updated',
  project_created: 'Project Created',
  project_updated: 'Project Updated',
  session_created: 'Session Created',
  session_bulk_imported: 'Bulk Import',
};

export const AuditLogPage = () => {
  const { isAdmin } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    action: '',
    actor_email: '',
    target_email: '',
  });

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.action) params.append('action', filters.action);
      if (filters.actor_email) params.append('actor_email', filters.actor_email);
      if (filters.target_email) params.append('target_email', filters.target_email);
      params.append('limit', '100');
      
      const response = await api.get(`/audit/?${params.toString()}`);
      setLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchStats = async () => {
    try {
      const response = await api.get('/audit/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch audit stats:', error);
    }
  };

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [fetchLogs]);

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActionDisplay = (action) => {
    const config = ACTION_ICONS[action] || { icon: Activity, color: 'text-gray-600', bg: 'bg-gray-100' };
    const Icon = config.icon;
    return (
      <div className={`inline-flex items-center gap-2 px-2 py-1 rounded-full ${config.bg}`}>
        <Icon className={`w-4 h-4 ${config.color}`} />
        <span className={`text-xs font-medium ${config.color}`}>
          {ACTION_LABELS[action] || action}
        </span>
      </div>
    );
  };

  if (!isAdmin()) {
    return (
      <div className="text-center py-12" data-testid="audit-log-page">
        <Shield className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">Access Restricted</h2>
        <p className="text-muted-foreground">Only administrators can view audit logs.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="audit-log-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
            <History className="w-6 h-6" />
            Audit Log
          </h1>
          <p className="text-muted-foreground mt-1">
            Track all system changes and user actions
          </p>
        </div>
        <Button onClick={fetchLogs} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <History className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_logs}</p>
                  <p className="text-sm text-muted-foreground">Total Events</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-lg">
                  <Activity className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.last_24_hours}</p>
                  <p className="text-sm text-muted-foreground">Last 24 Hours</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <UserCheck className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {(stats.by_action?.user_approved || 0) + (stats.by_action?.user_promoted_manager || 0)}
                  </p>
                  <p className="text-sm text-muted-foreground">User Approvals</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Select 
                value={filters.action || "all"} 
                onValueChange={(value) => setFilters(prev => ({ ...prev, action: value === "all" ? "" : value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  <SelectItem value="user_approved">User Approved</SelectItem>
                  <SelectItem value="user_denied">User Denied</SelectItem>
                  <SelectItem value="user_promoted_manager">Promoted to Manager</SelectItem>
                  <SelectItem value="user_promoted_superadmin">Promoted to Super Admin</SelectItem>
                  <SelectItem value="user_activated">User Activated</SelectItem>
                  <SelectItem value="user_deactivated">User Deactivated</SelectItem>
                  <SelectItem value="domain_added">Domain Added</SelectItem>
                  <SelectItem value="domain_removed">Domain Removed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Actor email..."
                value={filters.actor_email}
                onChange={(e) => setFilters(prev => ({ ...prev, actor_email: e.target.value }))}
                className="pl-10"
              />
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Target email..."
                value={filters.target_email}
                onChange={(e) => setFilters(prev => ({ ...prev, target_email: e.target.value }))}
                className="pl-10"
              />
            </div>
            <Button 
              variant="outline" 
              onClick={() => setFilters({ action: '', actor_email: '', target_email: '' })}
            >
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Audit Log Table */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="h-12 bg-muted animate-pulse rounded" />
              ))}
            </div>
          ) : logs.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-48">Timestamp</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Actor</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead>Description</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell className="whitespace-nowrap">
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        {formatTimestamp(log.timestamp)}
                      </div>
                    </TableCell>
                    <TableCell>
                      {getActionDisplay(log.action)}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-sm font-medium">{log.actor_email}</p>
                        <Badge variant="outline" className="text-xs mt-1">
                          {log.actor_role}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      {log.target_email ? (
                        <p className="text-sm">{log.target_email}</p>
                      ) : (
                        <span className="text-sm text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <p className="text-sm text-muted-foreground">{log.description}</p>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <History className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No audit logs found</h3>
              <p className="text-muted-foreground">
                {filters.action || filters.actor_email || filters.target_email
                  ? 'Try adjusting your filters'
                  : 'Audit logs will appear here as actions are performed'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogPage;
